import React, {
  useEffect, createContext, useState, useMemo, useCallback, useRef
} from 'react';
import {
  bool as boolType, object as objectType,
} from 'prop-types';
import { useDataModel } from '@thd-nucleus/data-sources';
import * as TileUtils from '../common/utils/TileUtils';
import * as CardUtils from '../common/utils/cardUtils';
import * as localData from '../core/Constants';
import * as MockUserData from '../core/Data';

export const DataContext = createContext({});

DataContext.propTypes = {
  isLoading: boolType,
  useData: objectType,
  tiles: objectType,
};

const determineLoadingState = (res) => {
  const { loading, data, error } = res;
  const states = {
    isLoadingInitial: loading === false && !(data || error),
    isLoadingActual: loading === true,
    isLoadingDone: loading === false && (data || error),
  };
  return states.isLoadingInitial || states.isLoadingActual;
};

const determineErrorState = (res) => {
  const data = res.data && (res.data[localData.graphqlEndpoint] || res.data[localData.availablePerksEndpoint]);
  const isUserDataEmpty = data && CardUtils.isObjectEmpty(data);
  return !determineLoadingState(res) && (isUserDataEmpty || res.error);
};

export const GatherMockData = (endpoint) => {
  let mockData = {};
  if (endpoint === 'welcomeCard') { mockData = { [localData.graphqlEndpoint]: MockUserData.DevUser379 }; }
  if (endpoint === 'availablePerks') mockData = { [localData.availablePerksEndpoint]: MockUserData.AvailablePerks };
  return [() => {}, { loading: false, data: mockData, error: null, called: true }];
};

export const DataProvider = ({ children }) => {
  const userDataRef = useRef({});
  const availablePerksDataRef = useRef({});

  const opts = {
    variables: {
      customerAccountId: CardUtils.THD_CUSTOMER()?.svocID,
      userId: CardUtils.THD_CUSTOMER()?.userID,
      channelId: 1,
    },
    skip: !CardUtils.THD_CUSTOMER()?.svocID,
    notifyOnNetworkStatusChange: true,
    context: { withAuth: true },
  };

  const response = useDataModel(localData.graphqlEndpoint, opts);
  const perksResponse = useDataModel(localData.availablePerksEndpoint, opts);
  userDataRef.current = response?.data && response?.data[localData.graphqlEndpoint];
  availablePerksDataRef.current = perksResponse?.data && perksResponse?.data[localData.availablePerksEndpoint];

  const refreshData = () => {
    response.refetch();
  };

  const refreshAvailablePerks = () => {
    perksResponse.refetch();
  };

  const isAdmin = CardUtils.getUserRole() === 'ADMIN';
  const userData = userDataRef.current;
  const availablePerksData = availablePerksDataRef.current;

  const contextValue = {
    isAdmin,
    isLoading: determineLoadingState(response),
    userDataError: determineErrorState(response) || !userData,
    userData,
    tiles: TileUtils.createTiles({
      isAdmin,
      userData,
      availablePerksData,
      isLoadingPerks: determineLoadingState(perksResponse),
      availablePerksError: determineErrorState(perksResponse) || !availablePerksData,
      refreshAvailablePerks
    }),
    refreshData,
  };

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
};

export const exportsForTesting = {
  GatherMockData,
};
